import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { format, addMonths, subMonths, setMonth, setYear, startOfMonth } from 'date-fns';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@/components/ui/select';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/lib/utils';

/**
 * Size tokens consumed by all sub-components.
 * "default"  → standard trigger + 2rem calendar cells + normal footer
 * "compact"  → tighter trigger + 1.75rem calendar cells + smaller footer
 */
function useSizeTokens(size) {
  const compact = size === 'compact';
  return {
    compact,
    // Trigger
    triggerH:    compact ? 'h-10'                             : 'h-10',
    // Month/Year select triggers
    selectH:     compact ? 'h-6 px-2 text-sm font-semibold'  : 'h-8 px-3 text-base font-semibold',
    // Nav arrow buttons
    arrowBtn:    compact ? 'h-6 w-6'                         : 'h-7 w-7',
    arrowIcon:   compact ? 'size-3'                          : 'size-3.5',
    // Calendar cell size
    calSize:     compact ? 'compact'                         : 'default',
    // Footer
    footerPad:   compact ? 'gap-1.5 px-2 py-2'              : 'gap-2 px-3 py-3',
    footerBtn:   compact ? 'h-6 px-3 text-xs rounded'        : 'h-8 px-4 text-sm rounded',
    // Nav row padding
    navPad:      compact ? 'px-2 py-2'                       : 'px-3 py-3',
    // NavItem dropdown items
    itemCls:     compact ? 'px-2 py-0.5 text-xs'             : 'px-2 py-0.5 text-sm',
  };
}

/** Compact select item with no tick mark, tight padding, small font. */
function NavItem({ value, children, itemCls, disabled }) {
  return (
    <SelectPrimitive.Item
      value={value}
      disabled={disabled}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm outline-none",
        "focus:bg-accent/15 focus:text-primary data-[state=checked]:font-semibold data-[state=checked]:text-primary",
        "data-[disabled]:opacity-30 data-[disabled]:pointer-events-none",
        itemCls,
      )}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Coerce a moment object, Date, or ISO/locale string to a native Date. */
function toNativeDate(val) {
  if (!val) return new Date();
  // moment object
  if (val && typeof val === 'object' && typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

// Short names shown in header trigger; full names in the dropdown list
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS  = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// ─── Custom navigation row (uses Radix Select — stays inside DismissableLayer) ──

/**
 * Month + Year select pair, styled as compact inline dropdowns (no visible border,
 * transparent background). Radix Select renders its portal inside the same
 * DismissableLayer context as the parent Popover, so opening it never closes the picker.
 */
function MonthYearSelects({ month, onChangeMonth, tokens }) {
  const now      = new Date();
  const fromYear = 2018;
  const toYear   = now.getFullYear();
  const years    = Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i);
  const maxMonthIdx = month.getFullYear() === toYear ? now.getMonth() : 11;

  const triggerCls = cn(
    "flex w-auto items-center gap-1",
    "border border-border rounded-md !shadow-sm outline-none",
    "ring-0 ring-offset-0 focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none",
    "bg-background hover:border-accent",
    tokens.selectH,
    "[&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-50 [&>svg]:shrink-0",
  );

  return (
    <div className="flex items-center gap-0.5">
      <Select
        value={month.getMonth().toString()}
        onValueChange={(v) => onChangeMonth(setMonth(month, parseInt(v)))}
      >
        <SelectTrigger className={triggerCls}>
          <span>{SHORT_MONTHS[month.getMonth()]}</span>
        </SelectTrigger>
        <SelectContent className="min-w-0 w-auto max-h-56 p-1 shadow-md">
          {SHORT_MONTHS.map((m, i) => (
            <NavItem key={i} value={i.toString()} itemCls={tokens.itemCls} disabled={i > maxMonthIdx}>{m}</NavItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={month.getFullYear().toString()}
        onValueChange={(v) => onChangeMonth(setYear(month, parseInt(v)))}
      >
        <SelectTrigger className={triggerCls}>
          <span>{month.getFullYear()}</span>
        </SelectTrigger>
        <SelectContent className="min-w-0 w-auto max-h-56 p-1 shadow-md">
          {years.map((y) => (
            <NavItem key={y} value={y.toString()} itemCls={tokens.itemCls}>{y}</NavItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CalendarNav({ leftMonth, rightMonth, onLeftChange, onRightChange, onPrev, onNext, tokens }) {
  const MIN_MONTH = new Date(2018, 0, 1);
  const MAX_MONTH = startOfMonth(new Date());

  const prevDisabled = leftMonth <= MIN_MONTH;
  const refMonth     = rightMonth ?? leftMonth;
  const nextDisabled = refMonth >= MAX_MONTH;

  const arrowCls = cn(
    "shrink-0 text-primary/70 hover:text-primary hover:bg-accent/10",
    "disabled:opacity-30 disabled:pointer-events-none",
    tokens.arrowBtn,
  );

  return (
    <div className={cn("flex items-center", tokens.navPad)}>
      {/* Left half */}
      <div className="flex flex-1 items-center">
        <Button variant="ghost" size="icon" onClick={onPrev} disabled={prevDisabled} className={arrowCls}>
          <ChevronLeftIcon className={tokens.arrowIcon} />
        </Button>
        <div className="flex flex-1 justify-center">
          <MonthYearSelects month={leftMonth} onChangeMonth={onLeftChange} tokens={tokens} />
        </div>
      </div>

      {/* Right half (dual mode) or just next arrow (single mode) */}
      {rightMonth ? (
        <div className="flex flex-1 items-center">
          <div className="flex flex-1 justify-center">
            <MonthYearSelects month={rightMonth} onChangeMonth={onRightChange} tokens={tokens} />
          </div>
          <Button variant="ghost" size="icon" onClick={onNext} disabled={nextDisabled} className={arrowCls}>
            <ChevronRightIcon className={tokens.arrowIcon} />
          </Button>
        </div>
      ) : (
        <Button variant="ghost" size="icon" onClick={onNext} disabled={nextDisabled} className={arrowCls}>
          <ChevronRightIcon className={tokens.arrowIcon} />
        </Button>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function DateRangePicker({ 
  // Flat props (all current call sites use this shape)
  startDate: startDateProp,
  endDate: endDateProp,
  onApply, 
  displayMode = 'unified',
  containerClass,
  /** Pass compact={true} for a tighter trigger + smaller calendar cells. */
  compact = false,
  /** 'default' | 'compact'. 'compact' gives a smaller trigger + tighter calendar/footer. */
  size: sizeProp,
  maxDate,
  minDate,
  singleDatePicker = false,
  // initialSettings object (alternative API, merged below)
  initialSettings = {},
  // Passthrough
  children,
  className,
  side = 'bottom',
  align = 'start',
  disabled = false,
  ...rest
}) {
  // Resolve size: explicit size prop wins; fall back to compact boolean
  const resolvedSize = sizeProp ?? (compact ? 'compact' : 'default');
  const tokens = useSizeTokens(resolvedSize);
  // Merge flat props with initialSettings (flat props take precedence)
  const resolvedStart  = startDateProp  ?? initialSettings.startDate;
  const resolvedEnd    = endDateProp    ?? initialSettings.endDate;
  const resolvedMax    = maxDate        ?? initialSettings.maxDate;
  const resolvedMin    = minDate        ?? initialSettings.minDate;
  const isSingle       = singleDatePicker || initialSettings.singleDatePicker || false;

  // Convert incoming values to native Date
  const initStart = toNativeDate(resolvedStart);
  const initEnd   = toNativeDate(resolvedEnd);

  // ── State ────────────────────────────────────────────────────────────────
  const [isOpen, setIsOpen]               = useState(false);
  const [committedStart, setCommittedStart] = useState(initStart);
  const [committedEnd, setCommittedEnd]     = useState(initEnd);

  // Pending selection while popover is open
  const [selectedRange, setSelectedRange]   = useState({ from: initStart, to: isSingle ? initStart : initEnd });
  const [rangeComplete, setRangeComplete]   = useState(true);
  const prevRangeRef = useRef({ from: initStart, to: isSingle ? initStart : initEnd });

  // Controlled month — left calendar always shows this, right shows next month
  const [leftMonth, setLeftMonth]  = useState(() => startOfMonth(initStart));

  const rightMonth = isSingle ? null : addMonths(leftMonth, 1);

  // Sync pending selection when popover opens
  useEffect(() => {
    if (isOpen) {
      const r = { from: committedStart, to: isSingle ? committedStart : committedEnd };
      setSelectedRange(r);
      prevRangeRef.current = r;
      setRangeComplete(true);
      setLeftMonth(startOfMonth(committedStart));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation ───────────────────────────────────────────────────────────

  const handlePrev = () => setLeftMonth((m) => subMonths(m, 1));
  const handleNext = () => setLeftMonth((m) => addMonths(m, 1));

  const handleLeftChange = (newMonth) => setLeftMonth(startOfMonth(newMonth));

  const handleRightChange = (newMonth) => {
    // Right month = leftMonth + 1; adjust left to keep right on the selected month
    setLeftMonth(startOfMonth(subMonths(newMonth, 1)));
  };

  // ── Day selection ─────────────────────────────────────────────────────────

  const handleSelect = (range) => {
    if (isSingle) {
      const d = range?.from || range;
      if (d) {
        setSelectedRange({ from: d, to: d });
        prevRangeRef.current = { from: d, to: d };
        setRangeComplete(true);
      }
      return;
    }

    const wasComplete = rangeComplete;
    const prev        = prevRangeRef.current;

    if (!range?.from) {
      setSelectedRange({ from: undefined, to: undefined });
      prevRangeRef.current = { from: undefined, to: undefined };
      setRangeComplete(false);
      return;
    }

    if (wasComplete && prev.from && prev.to) {
      // User clicking again after a full range — start a new selection
      let clicked = range.from;
      if (range.to) {
        const fromMoved = range.from.getTime() !== prev.from.getTime();
        const toMoved   = range.to.getTime()   !== prev.to.getTime();
        if (toMoved)   clicked = range.to;
        else if (fromMoved) clicked = range.from;
      }
      const next = { from: clicked, to: undefined };
      setSelectedRange(next);
      prevRangeRef.current = next;
      setRangeComplete(false);
    } else if (range.from && range.to) {
      setSelectedRange(range);
      prevRangeRef.current = range;
      setRangeComplete(true);
    } else {
      const next = { from: range.from, to: undefined };
      setSelectedRange(next);
      prevRangeRef.current = next;
      setRangeComplete(false);
    }
  };

  // ── Apply / Cancel ────────────────────────────────────────────────────────

  const handleApply = () => {
    const from = selectedRange.from;
    const to   = isSingle ? selectedRange.from : selectedRange.to;
    if (!from || (!isSingle && !to)) return;

    setCommittedStart(from);
    if (!isSingle) setCommittedEnd(to);

    if (onApply) {
      // Return moment objects so all existing callers (.format(), .toDate()) work unchanged
      onApply({}, {
        startDate: moment(from),
        endDate:   moment(isSingle ? from : to),
        oldEndDate: moment(committedStart),
      });
    }
    setIsOpen(false);
  };

  const handleCancel = () => setIsOpen(false);

  // ── Formatting ────────────────────────────────────────────────────────────

  // Display dates in MM/DD/YYYY format matching the rest of the app
  const dateFmt = 'MM/dd/yyyy';

  const fmtStart = format(committedStart, dateFmt);
  const fmtEnd   = format(committedEnd,   dateFmt);

  // ── Disabled dates ────────────────────────────────────────────────────────
  // Default max is today — future dates are always disabled unless caller passes a future maxDate

  const effectiveMax = toNativeDate(resolvedMax ?? new Date());
  const disabledMatcher = resolvedMin
    ? { before: toNativeDate(resolvedMin), after: effectiveMax }
    : { after: effectiveMax };

  // ── Render ────────────────────────────────────────────────────────────────

  const applyDisabled = isSingle
    ? !selectedRange.from
    : !selectedRange.from || !selectedRange.to;

  // Default trigger — "From MM/DD/YYYY ▾  To MM/DD/YYYY ▾" matching original UI
  const defaultTrigger = (
    <div className={cn("flex items-center", containerClass, className)}>
      {isSingle ? (
        /* Single date picker — one pill */
        <button
          type="button"
              className={cn(
            "flex items-center gap-1.5 rounded-md border border-border bg-background px-3 text-sm text-primary",
            "transition-colors hover:border-accent hover:bg-accent/5",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            tokens.triggerH,
          )}
        >
          <CalendarIcon className="shrink-0 size-4 text-accent" />
          <span className="font-medium">{fmtStart}</span>
          <ChevronDownIcon className={cn("shrink-0 size-3.5 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
        </button>
      ) : (
        /* Range picker — From … | To … in one bordered container */
        <div className={cn(
          "flex items-stretch overflow-hidden rounded-md border border-border bg-background text-sm transition-colors hover:border-accent",
          tokens.triggerH,
          isOpen && "border-accent",
        )}>
          {/* From */}
          <div className="flex items-center gap-1.5 border-r border-border px-3">
            <span className="text-xs text-muted-foreground select-none font-normal">From</span>
            <span className="font-semibold text-primary">{fmtStart}</span>
            <ChevronDownIcon className={cn("shrink-0 size-3.5 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
          </div>
          {/* To */}
          <div className="flex items-center gap-1.5 px-3">
            <span className="text-xs text-muted-foreground select-none font-normal">To</span>
            <span className="font-semibold text-primary">{fmtEnd}</span>
            <ChevronDownIcon className={cn("shrink-0 size-3.5 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={(open) => { if (!disabled) setIsOpen(open); }}>
      <PopoverTrigger asChild disabled={disabled}>
        <div className={cn(disabled && "opacity-50 cursor-not-allowed pointer-events-none")}>
          {children || defaultTrigger}
        </div>
        </PopoverTrigger>

        <PopoverContent
        className={cn("w-auto p-0 !shadow-xl border-border", rest.popoverClassName)}
        align={align}
        side={side}
        onInteractOutside={(e) => {
          const target = e.target;
          if (target?.closest('[data-radix-select-content]') ||
              target?.closest('[data-radix-popper-content-wrapper]')) {
            e.preventDefault();
          }
        }}
      >
        {/* ── Navigation header ── */}
        <CalendarNav
          leftMonth={leftMonth}
          rightMonth={rightMonth}
          onLeftChange={handleLeftChange}
          onRightChange={handleRightChange}
          onPrev={handlePrev}
          onNext={handleNext}
          tokens={tokens}
        />

        {/* ── Calendar ── */}
        <div className="border-t border-border/50">
          <Calendar
            mode={isSingle ? 'single' : 'range'}
            selected={isSingle ? selectedRange.from : selectedRange}
            onSelect={handleSelect}
            month={leftMonth}
            onMonthChange={setLeftMonth}
            numberOfMonths={isSingle ? 1 : 2}
            disabled={disabledMatcher}
            captionLayout="label"
            components={{
              Nav: () => null,
              MonthCaption: () => null,
            }}
            size={tokens.calSize}
            className={cn(
              "rounded-none border-0",
              rest.calendarClassName,
            )}
            classNames={rest.calendarClassNames}
          />
        </div>

        {/* ── Footer ── */}
        <div className={cn(
          "flex items-center justify-end border-t border-border/50 bg-muted/20",
          tokens.footerPad,
        )}>
          <Button
            onClick={handleCancel}
            className={cn("bg-primary text-white hover:bg-primary", tokens.footerBtn)}
          >
            Cancel
            </Button>
            <Button 
              onClick={handleApply}
            disabled={applyDisabled}
            className={cn(
              "bg-accent text-white hover:bg-accent/90 disabled:opacity-40",
              tokens.footerBtn,
            )}
          >
            Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
  );
}
